// utils/apiUtils.js - Enhanced with measurements support
export const apiUtils = {
  // Check if an error is retryable
  isRetryableError: (error) => {
    if (!error) return false;

    // Network errors are retryable
    if (error.name === 'NetworkError' || error.message?.includes('fetch')) return true;

    // Timeout errors are retryable
    if (error.name === 'TimeoutError' || error.message?.includes('timeout')) return true;

    // Server errors (5xx) are retryable
    if (error.status >= 500) return true;

    // Rate limit errors might be retryable after a delay
    if (error.status === 429) return true;

    return false;
  },

  // Format error messages for user display
  formatErrorMessage: (error) => {
    if (!error) return 'An unknown error occurred';

    // Handle network errors
    if (error.name === 'NetworkError' || error.message?.includes('fetch')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    // Handle timeout errors
    if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
      return 'The request timed out. The server might be busy, please try again.';
    }

    // Handle HTTP status codes
    switch (error.status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication required. Please log in and try again.';
      case 403:
        return 'Access denied. You don\'t have permission to access this resource.';
      case 404:
        return 'Resource not found. The requested data might have been removed or moved.';
      case 409:
        return 'Conflict detected. The resource might have been modified by another user.';
      case 422:
        return 'Invalid data provided. Please check your input and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Internal server error. Please try again later.';
      case 502:
        return 'Bad gateway. The server is temporarily unavailable.';
      case 503:
        return 'Service unavailable. The server is temporarily down for maintenance.';
      case 504:
        return 'Gateway timeout. The server took too long to respond.';
      default:
        if (error.status >= 400 && error.status < 500) {
          return `Client error (${error.status}): ${error.message || 'Invalid request'}`;
        } else if (error.status >= 500) {
          return `Server error (${error.status}): Please try again later`;
        }
    }

    // Handle specific API error messages
    if (error.message) {
      return error.message;
    }

    return 'An unexpected error occurred. Please try again.';
  },

  // Build query string from parameters
  buildQueryString: (params) => {
    if (!params || Object.keys(params).length === 0) return '';

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  },

  // Parse API response to extract meaningful data
  parseApiResponse: (response, type = 'default') => {
    if (!response) return null;

    switch (type) {
      case 'measurements':
        return {
          box_id: response.box_id,
          measurement_id: response.measurement_id,
          timestamp: response.timestamp,
          measurement_data: response.measurement_data || {},
          sensor_count: response.measurement_data
            ? Object.keys(response.measurement_data).length
            : 0,
          active_sensor_count: response.measurement_data
            ? Object.entries(response.measurement_data).filter(([_, data]) =>
                data && Object.keys(data).length > 0
              ).length
            : 0
        };

      case 'boxes':
        if (Array.isArray(response)) {
          return response.map(box => ({
            ...box,
            id: box.box_id || box.id,
            title: box.title || box.name || `Box ${box.box_id}`,
            description: box.description || 'N/A',
            location: box.location || 'Unknown',
            assigned_to: box.assigned_to || 'Unassigned',
            status: box.status || 'unknown',
            score: typeof box.score === 'number' ? box.score : 0,
            updated_at: box.updated_at || new Date().toISOString()
          }));
        }
        return response;

      default:
        return response;
    }
  },

  // Validate measurement data structure
  validateMeasurementData: (data) => {
    if (!data) return { valid: false, error: 'No data provided' };

    if (!data.box_id) {
      return { valid: false, error: 'Missing box_id' };
    }

    if (!data.measurement_data) {
      return { valid: false, error: 'Missing measurement_data' };
    }

    if (typeof data.measurement_data !== 'object') {
      return { valid: false, error: 'measurement_data must be an object' };
    }

    return { valid: true };
  },

  // Format timestamp for display
  formatTimestamp: (timestamp) => {
    if (!timestamp) return 'Unknown';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid date';

      return date.toLocaleString();
    } catch (error) {
      return 'Invalid date';
    }
  },

  // Calculate data freshness
  getDataFreshness: (timestamp) => {
    if (!timestamp) return 'unknown';

    try {
      const now = new Date();
      const dataTime = new Date(timestamp);
      const diffMs = now - dataTime;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffMinutes < 1) return 'just now';
      if (diffMinutes < 60) return `${diffMinutes} minutes ago`;

      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours < 24) return `${diffHours} hours ago`;

      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days ago`;
    } catch (error) {
      return 'unknown';
    }
  },

  // Handle API errors gracefully
  handleApiError: (error, context = '') => {
    console.error(`API Error${context ? ` in ${context}` : ''}:`, error);

    // Log additional context for debugging
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
    }

    return {
      message: apiUtils.formatErrorMessage(error),
      status: error.status || 0,
      retryable: apiUtils.isRetryableError(error),
      timestamp: new Date().toISOString()
    };
  }
};